import React, { useState, useRef, useMemo } from "react";
import { CCard, CCardHeader, CCardBody, CButtonGroup, CButton, CSpinner } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilSave, cilActionUndo, cilTrash } from "@coreui/icons";
import { useTranslation } from "react-i18next";
import { getInsertionIndex } from "../../utils/cameraUtils";

const RoiEditor = ({ snapshotUrl, polygonPoints, setPolygonPoints, isLive, showRoi, onSave, onReset, onClear, cameraName, onImageLoad, readOnly = false, operatingMode }) => {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [draggingPoint, setDraggingPoint] = useState(null);
  
  const isInteractive = showRoi && !isLive && !readOnly;
  
  // Определяем, является ли текущий режим режимом рисования линии (Подсчет посетителей)
  const isLineMode = operatingMode === 'visitor_counting';

  const isModified = useMemo(() => {
    if (!polygonPoints || !polygonPoints.initial || !polygonPoints.current) return false;
    return JSON.stringify(polygonPoints.initial) !== JSON.stringify(polygonPoints.current);
  }, [polygonPoints]);

  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setImageSize({ width: naturalWidth, height: naturalHeight });
    if (onImageLoad) onImageLoad({ naturalWidth, naturalHeight });
  };

  const handleCanvasMouseDown = (e) => {
    if (!isInteractive || !svgRef.current || imageSize.width === 0) return;
    e.preventDefault();
    
    const currentPoints = polygonPoints.current || [];

    // Блокируем создание 3-й точки, если это режим линии
    if (isLineMode && currentPoints.length >= 2) return;

    const rect = svgRef.current.getBoundingClientRect();
    
    // ИСПРАВЛЕНИЕ: Берем реальные размеры SVG в момент клика
    const scaleX = imageSize.width / rect.width;
    const scaleY = imageSize.height / rect.height;
    
    const newPoint = { 
      x: (e.clientX - rect.left) * scaleX, 
      y: (e.clientY - rect.top) * scaleY 
    };
    
    // В режиме линии просто добавляем точку в конец массива, иначе - ищем место для вставки в полигон
    const insertIndex = isLineMode ? currentPoints.length : getInsertionIndex(newPoint, currentPoints);
    const newPointsList = [...currentPoints];
    newPointsList.splice(insertIndex, 0, newPoint);
    
    setPolygonPoints({ ...polygonPoints, current: newPointsList });
    setDraggingPoint(insertIndex);
  };

  const handlePointMouseDown = (e, index) => {
    e.stopPropagation(); 
    e.preventDefault();
    if (!isInteractive) return;
    setDraggingPoint(index);
  };

  const handleSvgMouseMove = (e) => {
    if (draggingPoint === null || !svgRef.current) return;
    e.preventDefault();
    
    const rect = svgRef.current.getBoundingClientRect();
    
    // ИСПРАВЛЕНИЕ: Берем реальные размеры SVG при перетаскивании
    const scaleX = imageSize.width / rect.width;
    const scaleY = imageSize.height / rect.height;
    
    const x = Math.max(0, Math.min(imageSize.width, (e.clientX - rect.left) * scaleX));
    const y = Math.max(0, Math.min(imageSize.height, (e.clientY - rect.top) * scaleY));
    
    const newPoints = [...polygonPoints.current];
    newPoints[draggingPoint] = { x, y };
    setPolygonPoints({ ...polygonPoints, current: newPoints });
  };

  const handleSvgMouseUp = () => setDraggingPoint(null);
  
  const handlePointRightClick = (e, indexToDelete) => {
    e.preventDefault(); 
    e.stopPropagation();
    if (!isInteractive) return;
    setPolygonPoints({ ...polygonPoints, current: polygonPoints.current.filter((_, index) => index !== indexToDelete) });
  };

  const pointsToRender = polygonPoints?.current || [];

  return (
    <CCard className="mb-4">
      <CCardHeader>{t("camera.title_roi")} <strong>{cameraName ? `- ${cameraName}` : ""}</strong></CCardHeader>
      <CCardBody>
        {snapshotUrl ? (
          <>
            <div style={{ position: "relative", cursor: isInteractive ? "crosshair" : "default", width: "100%", backgroundColor: "#000" }}>
              <img 
                src={snapshotUrl} 
                alt="Snapshot" 
                style={{ width: "100%", height: "auto", display: "block", pointerEvents: "none" }} 
                onLoad={handleImageLoad} 
              />
              
              <svg 
                ref={svgRef} 
                width="100%" 
                height="100%" 
                viewBox={`0 0 ${imageSize.width} ${imageSize.height}`} 
                style={{ position: "absolute", top: 0, left: 0, pointerEvents: imageSize.width > 0 ? "auto" : "none" }} 
                onMouseDown={handleCanvasMouseDown} 
                onMouseMove={handleSvgMouseMove} 
                onMouseUp={handleSvgMouseUp} 
                onMouseLeave={handleSvgMouseUp}
              >
                
                {/* 1. ЕСЛИ ЭТО РЕЖИМ ЛИНИИ И РОВНО 2 ТОЧКИ (СИНЯЯ ЛИНИЯ) */}
                {showRoi && isLineMode && pointsToRender.length === 2 && (
                  <g>
                    {/* Сама линия */}
                    <line 
                      x1={pointsToRender[0].x} y1={pointsToRender[0].y} 
                      x2={pointsToRender[1].x} y2={pointsToRender[1].y} 
                      style={{ stroke: "#321fdb", strokeWidth: 5, vectorEffect: "non-scaling-stroke", pointerEvents: "none" }} 
                    />
                    
                    {/* Вычисляем нормаль для отрисовки стрелочки направления "ВХОД" */}
                    {(() => {
                      const dx = pointsToRender[1].x - pointsToRender[0].x;
                      const dy = pointsToRender[1].y - pointsToRender[0].y;
                      const cx = (pointsToRender[0].x + pointsToRender[1].x) / 2;
                      const cy = (pointsToRender[0].y + pointsToRender[1].y) / 2;
                      
                      // Вектор нормали (повернутый на 90 градусов)
                      // Математика совпадает с backend'ом (cross_product)
                      const len = Math.sqrt(dx*dx + dy*dy);
                      const nx = -dy / len;
                      const ny = dx / len;
                      
                      const arrowLen = 50;
                      const endX = cx + nx * arrowLen;
                      const endY = cy + ny * arrowLen;

                      // Крылья стрелки
                      const wingLen = 15;
                      const angle = Math.atan2(ny, nx);
                      const w1x = endX - wingLen * Math.cos(angle - Math.PI/6);
                      const w1y = endY - wingLen * Math.sin(angle - Math.PI/6);
                      const w2x = endX - wingLen * Math.cos(angle + Math.PI/6);
                      const w2y = endY - wingLen * Math.sin(angle + Math.PI/6);

                      return (
                        <g style={{ pointerEvents: "none" }}>
                          {/* Стержень стрелки */}
                          <line x1={cx} y1={cy} x2={endX} y2={endY} stroke="#2eb85c" strokeWidth="4" />
                          {/* Крылья */}
                          <line x1={endX} y1={endY} x2={w1x} y2={w1y} stroke="#2eb85c" strokeWidth="4" />
                          <line x1={endX} y1={endY} x2={w2x} y2={w2y} stroke="#2eb85c" strokeWidth="4" />
                          {/* Подпись */}
                          <text x={endX + nx*15} y={endY + ny*15} fill="#2eb85c" fontSize="20" fontWeight="bold" textAnchor="middle" alignmentBaseline="middle" style={{ textShadow: "1px 1px 2px black" }}>
                            ВХОД
                          </text>
                        </g>
                      );
                    })()}
                  </g>
                )}

                {/* 2. ЕСЛИ ЭТО РЕЖИМ ПОЛИГОНА (ОБЫЧНАЯ КРАСНАЯ ЗОНА) */}
                {showRoi && !isLineMode && pointsToRender.length > 0 && (
                  <polygon 
                    points={pointsToRender.map((p) => `${p.x},${p.y}`).join(" ")} 
                    style={{ fill: "rgba(255, 0, 0, 0.3)", stroke: "red", strokeWidth: 4, vectorEffect: "non-scaling-stroke", pointerEvents: "none" }} 
                  />
                )}

                {/* ТОЧКИ (МАРКЕРЫ УПРАВЛЕНИЯ) */}
                {showRoi && pointsToRender.map((p, i) => (
                  <circle 
                    key={i} cx={p.x} cy={p.y} r="8" 
                    fill="white" 
                    stroke={isLineMode ? "#321fdb" : "red"} 
                    strokeWidth="2" 
                    onMouseDown={(e) => handlePointMouseDown(e, i)} 
                    onContextMenu={(e) => handlePointRightClick(e, i)} 
                    style={{ cursor: isLive ? "not-allowed" : "move", vectorEffect: "non-scaling-stroke" }} 
                  />
                ))}
              </svg>
            </div>

            {showRoi && !readOnly && (
              <CButtonGroup className="mt-3 w-100">
                <CButton color="primary" onClick={onSave} disabled={!isModified || isLive}>
                  <CIcon icon={cilSave} className="me-2" /> {t("common.save")}
                </CButton>
                <CButton color="secondary" onClick={onReset} disabled={!isModified || isLive}>
                  <CIcon icon={cilActionUndo} className="me-2" /> {t("common.reset")}
                </CButton>
                <CButton color="danger" variant="outline" onClick={onClear} disabled={isLive || pointsToRender.length === 0}>
                  <CIcon icon={cilTrash} className="me-2" /> {t("common.clear")}
                </CButton>
              </CButtonGroup>
            )}
            
            <small className="form-text text-muted mt-2 d-block">
              {isLineMode 
                ? "Для подсчета посетителей нарисуйте ЛИНИЮ ПЕРЕСЕЧЕНИЯ ровно из 2-х точек. Правый клик — удалить точку."
                : t("camera.roi_hint")
              }
            </small>
          </>
        ) : (
          <div className="text-center p-3"><CSpinner /></div>
        )}
      </CCardBody>
    </CCard>
  );
};

export default RoiEditor;